import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { ppmpItemsApi } from '../procurementCatalogsApi';
import type { PpmpItemCatalogEntry } from '../procurementCatalogTypes';
import { samplePpmpItem } from '../utils/samplePpmpItem';

/**
 * Create or edit one entry in the catalog a procurement line's description is drawn from.
 */
export function PpmpItemModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PpmpItemCatalogEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [description, setDescription] = useState(editing?.itemDescription ?? '');
  const [unit, setUnit] = useState(editing?.unit ?? '');
  const [cost, setCost] = useState(
    editing?.estimatedUnitCost == null ? '' : String(editing.estimatedUnitCost),
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    AN "EMPTY" RICH-TEXT DOCUMENT IS `<p></p>`, NOT AN EMPTY STRING, so a length check on the
    markup would call an untouched editor filled. Stripping the tags is what asks the question
    the reader would — is there anything written here — and it is the same test the server's own
    validator makes on the visible text.
  */
  const describedValid = description.replace(/<[^>]*>/g, '').trim().length > 0;
  const costValid = cost.trim() === '' || Number(cost) >= 0;
  const valid = describedValid && unit.trim().length > 0 && costValid;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      itemDescription: description,
      unit: unit.trim(),
      // BLANK IS NULL, never zero: nobody has priced it is a different claim from free, and the
      // column keeps the two apart.
      estimatedUnitCost: cost.trim() === '' ? null : Number(cost),
    };

    try {
      if (editing) await ppmpItemsApi.update(editing.id, request);
      else await ppmpItemsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this catalog item.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppmp-item' });
    } finally {
      setSaving(false);
    }
  }

  /*
    THE DESCRIPTION IS LEFT ALONE, which is the generator's own rule rather than an omission:
    the editor is uncontrolled after mount, so setting the state behind it leaves the value and
    the screen disagreeing. What it fills is everything the primary action is gated on besides
    it — and the reader writes the one field only they can.
  */
  function generate() {
    const sample = samplePpmpItem();

    setUnit(sample.unit);
    setCost(sample.estimatedUnitCost);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.itemDescriptionText}` : 'New catalog item'}
      size="3xl"
      id="ppmp-item-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'ppmp-item-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppmp-item-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppmp-item-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ppmp-item-unit"
            label="Unit"
            required
            description="What one of it is counted in — pc, box, lot."
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            error={unit.trim() ? error?.fieldError('unit') : 'Enter the unit it is counted in.'}
            disabled={saving}
          />

          <TextField
            id="ppmp-item-estimated-unit-cost"
            label="Estimated unit cost"
            type="number"
            description="What one costs, as last known. Leave it blank if nobody has priced it — blank is not zero."
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            error={costValid ? error?.fieldError('estimatedUnitCost') : 'A cost cannot be negative.'}
            disabled={saving}
          />
        </div>

        {/*
          A DOCUMENT rather than a line, and it is the case that rule was written for: a
          procurement specification is routinely a table of dimensions and a list of what is in
          the box. Sanitized server-side on every write, which is the one gate that makes
          rendering it as HTML on the profile safe.
        */}
        <LazyRichTextField
          id="ppmp-item-description"
          label="Item description"
          required
          description="The specification, as it should read on a procurement plan. Headings, lists and a table are all available."
          value={description}
          onChange={setDescription}
          disabled={saving}
          error={
            describedValid ? error?.fieldError('itemDescription') : 'Describe the item.'
          }
        />
      </div>
    </Modal>
  );
}
